import { Controller } from "@hotwired/stimulus"
import { hotkeySteps, hotkeysEnabled } from "./hotkey_controller"

// Cheat sheet for the keyboard shortcuts available on the current page.
//
// The list is built from the DOM (`[data-hotkey]`) rather than from a registry, so
// it always shows exactly the shortcuts this account can actually use — bindings
// live on elements that are only rendered when policy/tenant gating allows them.
// It is built when the dialog opens (dialog-opener fires `dialog:open`), not on
// connect: rebuilding it on every Turbo visit is work thrown away unseen.
export default class extends Controller {
  static targets = ["list", "empty", "disabledNotice"]

  connect() {
    // Restore from Turbo snapshot cache or browser bfcache: a <dialog>'s `open`
    // attribute survives the cache, but its modal top-layer does not — so a restored
    // open dialog would render as inline content over a page whose scroll is still
    // locked. Same fix as timesheet_dialog_controller.
    if (this.element.hasAttribute("open")) {
      this.element.close()
      this.releaseScrollLock()
    }
  }

  renderList() {
    const entries = Array.from(document.querySelectorAll("[data-hotkey]")).filter(
      (element) => element.dataset.hotkeyLabel,
    )

    this.listTarget.replaceChildren(...entries.map((element) => this.rowFor(element)))
    this.emptyTarget.classList.toggle("hidden", entries.length > 0)

    // With shortcuts switched off every binding is inert, so the sheet must not read
    // as a list of things to press — it is still reachable, since the menu entry is
    // the only way back to the toggle.
    const enabled = hotkeysEnabled()
    this.disabledNoticeTarget.classList.toggle("hidden", enabled)
    this.listTarget.classList.toggle("opacity-50", !enabled)
  }

  rowFor(element) {
    const row = document.createElement("li")
    row.className = "flex items-center justify-between gap-4 py-2"

    const label = document.createElement("span")
    label.className = "text-sm text-gray-700"
    label.textContent = element.dataset.hotkeyLabel
    row.append(label)

    const keys = document.createElement("span")
    keys.className = "flex shrink-0 items-center gap-1"
    // One chip per key, whether the shortcut is a sequence (G then S) or a chord
    // (Mod+Shift+Z). hotkeySteps resolves `Mod` to the key this platform actually binds,
    // so the sheet never tells a Mac user to press Strg. The extra gap between steps is
    // what distinguishes "press G, then S" from "hold both".
    for (const [index, step] of hotkeySteps(element.dataset.hotkey).entries()) {
      for (const [position, key] of step.entries()) {
        const kbd = document.createElement("kbd")
        // Widened gap before each step after the first, so "press G, then S" is not read
        // as one chord — the keys of a chord stay tight together.
        kbd.className = "hotkey-kbd hotkey-kbd-sheet"
        if (index > 0 && position === 0) kbd.classList.add("ml-2")
        kbd.textContent = key
        keys.append(kbd)
      }
    }
    row.append(keys)

    return row
  }

  close() {
    this.element.close()
  }

  // Fires for the close button, Esc and backdrop dismissal alike — dialog-opener
  // locks body scrolling when it opens, so it has to be released here.
  onClose() {
    this.releaseScrollLock()
  }

  // The `?` shortcut can stack this dialog on top of another one (the support dialog
  // locks scrolling too). Esc closes only the topmost, so releasing unconditionally
  // would let the page scroll behind the modal still open underneath.
  releaseScrollLock() {
    if (document.querySelector("dialog[open]")) return

    document.body.style.overflow = ""
  }
}
